import React from 'react';
import { useOutletContext, Navigate, Outlet } from 'react-router-dom';
import { getFirstAllowedReportPath } from './reportAccessUtils';

interface RequireReportAccessProps {
  reportSlug: string;
}

interface AppOutletContext {
  userData?: {
    role?: string;
    allowedReports?: string[];
  };
}

const RequireReportAccess: React.FC<RequireReportAccessProps> = ({ reportSlug }) => {
  const outletContext = useOutletContext<AppOutletContext>();
  const userData = outletContext?.userData;

  // Admin-tier roles are never restricted. Broker-family roles (broker,
  // broker_assistant, credit_analyst) honour their own allowedReports list so a
  // linked analyst's report access carries across to the brokers they service.
  // office_admin joins them: it is the one admin-tier role that can be given a
  // per-user grant list. Inert until one exists - the empty-list fall-through
  // below already grants full access, and no office_admin carries a list today.
  if (
    userData?.role &&
    userData.role !== 'broker' &&
    userData.role !== 'broker_assistant' &&
    userData.role !== 'credit_analyst' &&
    userData.role !== 'office_admin'
  ) {
    return <Outlet context={outletContext} />;
  }

  // Broker with no restrictions → full access
  if (!userData?.allowedReports || !Array.isArray(userData.allowedReports) || userData.allowedReports.length === 0) {
    return <Outlet context={outletContext} />;
  }

  // Broker with restrictions → check if this report is allowed
  if (userData.allowedReports.includes(reportSlug)) {
    return <Outlet context={outletContext} />;
  }

  // Redirect to first allowed report (not "/" which would loop back)
  const target = getFirstAllowedReportPath(userData.allowedReports);
  return <Navigate to={target} replace />;
};

export default RequireReportAccess;
